<template>
  <div
    class="toolbar"
    v-if="$can('update', 'Dashboard_' + currentDash) || $can('manageUsers', 'Dashboard_' + currentDash)"
  >
    <span
      class="toolbar__showTrigger"
      :class="{ '_hiddenTrigger': switcherShown }"
      @click="switcherShown = true"
      >
      <icon name="ellipsis-v" />
    </span>
    <div
      class="toolbar-switcher"
      :class="{ '_expanded': switcherShown }"
      >
      <a
        href="#"
        class="toolbar__hideTrigger"
        @click.prevent="closeAll"
        >
        <span>close</span>
        <icon name="remove" />
      </a>
      <a
        v-for="(item, idx) in menu"
        v-if="checkAccess(item)"
        :key="idx"
        href="#"
        class="toolbar-switcher__link"
        v-b-tooltip.hover.right
        :title="item.title"
        @click.prevent="activateItem(item.clickHandler)"
        >
        <icon :name="item.icon" />
      </a>
    </div>
    <div class="toolbar-content" :class="{ '_active': activeToolbarItem }">
      <a
        href="#"
        class="toolbar-content__collapse"
        @click.prevent="collapseToolbarContent"
        >
        <span>collapse</span>
        <icon name="arrow-right" />
      </a>
      <div>
        <ComputedProps v-if="activeToolbarItem === toolbarItems[0]" />
        <CustomCode v-if="activeToolbarItem === toolbarItems[1]" />
      </div>
    </div>
    <AddWidgetModal />
    <NewSheetModal />
    <ShareDashboardModal />
    <PivotTableModal />
    <CustomCodeModal />
    <CustomCodeUpdateModal />
    <WidgetsDataModal :widgetsData="widgetsData" />
    <AddDataWidget />
    <EditDataWidget :widgetsData="widgetsData" />
    <AddChartModal />
    <InviteNewUserModal />
    <DashboardACModal />
  </div>
</template>

<script>
import { mapState } from 'vuex'
// components
import ComputedProps from './ComputedProps'
import CustomCode from './CustomCode'
import AddWidgetModal from '@/components/Modals/AddWidget'
import NewSheetModal from '@/components/Modals/NewSheet'
import ShareDashboardModal from '@/components/Modals/ShareDashboard'
import PivotTableModal from '@/components/Modals/PivotTable'
import CustomCodeModal from '@/components/Modals/CustomCode'
import CustomCodeUpdateModal from '@/components/Modals/CustomCodeUpdate'
import WidgetsDataModal from '@/components/Modals/WidgetsData'
import AddDataWidget from '@/components/Modals/AddDataWidget'
import EditDataWidget from '@/components/Modals/EditDataWidget'
import AddChartModal from '@/components/Modals/AddChart'
import InviteNewUserModal from '@/components/Modals/InviteNewUser'
import DashboardACModal from '@/components/Modals/DashboardAC'
// helpers
import eventBus from '@/helpers/event-bus'
// services
import db from '@/services/db'

import menuTree from './menuTree'

export default {
  components: {
    ComputedProps,
    CustomCode,
    AddWidgetModal,
    NewSheetModal,
    ShareDashboardModal,
    PivotTableModal,
    CustomCodeModal,
    CustomCodeUpdateModal,
    WidgetsDataModal,
    AddDataWidget,
    EditDataWidget,
    AddChartModal,
    InviteNewUserModal,
    DashboardACModal
  },
  data () {
    return {
      menu: menuTree,
      widgetsData: '{}',
      switcherShown: false,
      contentShown: false
    }
  },
  computed: {
    ...mapState([
      'currentTab',
      'activeToolbarItem',
      'toolbarItems',
      'currentDash',
      'dashOwner',
      'userId'
    ])
  },
  methods: {
    addWidget () {
      eventBus.$emit('showAddWidgetModal')
    },
    showComputed () {
      this.$store.commit('setActiveToolbarItem', 0)
    },
    addSpreadsheet () {
      eventBus.$emit('addSpreadsheet')
    },
    addChart () {
      eventBus.$emit('showAddChartModal')
    },
    addPivotTable () {
      eventBus.$emit('showAddPivotTableModal')
    },
    showDataTree () {
      eventBus.$emit('showWidgetsDataModal')
    },
    addDataWidget () {
      eventBus.$emit('showAddDataWidgetModal')
    },
    shareDashboard () {
      eventBus.$emit('shareDashboard')
    },
    showCustomCode () {
      this.$store.commit('setActiveToolbarItem', 1)
    },
    inviteNewUser () {
      eventBus.$emit('showInviteNewUserModal')
    },
    editDashboardAccess () {
      eventBus.$emit('showDashboardACModal')
    },

    // helper functions
    collapseToolbarContent () {
      this.$store.commit('removeActiveToolbarItem')
    },
    checkAccess (link) {
      if (link.isDashOwner) {
        if (this.dashOwner !== this.userId) return false
      } else if (link.isAdmin) {
        return this.$can('all', 'Admin')
      } else if (link.isModerator) {
        return this.$can('update', 'Dashboard_' + this.currentDash)
      } else if (link.isUserManager) {
        return this.$can('manageUsers', 'Dashboard_' + this.currentDash)
      }

      return true
    },
    activateItem (handler) {
      this.$emit('clickHandler', handler)
    },
    closeAll () {
      this.switcherShown = false
      this.collapseToolbarContent()
    }
  },
  created () {
    this.$on('clickHandler', (eventName) => {
      this[eventName]()
    })

    db.widgets.readData((data) => {
      this.widgetsData = data
    })
  },
  beforeDestroy () {
    this.contentShown = false
  }
}
</script>

<style lang="scss" scoped>
@import '~$scss/globals';

.toolbar {
  position: relative;
  position: fixed;
  right: 0;
  top: 0;
  background: white;
  z-index: 1;

  @keyframes expandTriggerShow {
    from { opacity: 0 }
    to { opacity: 1; }
  }
  @keyframes expandTriggerHide {
    0% { opacity: 1; }
    1 { opacity: 0; }
  }

  &__showTrigger,
  &__hideTrigger {
    cursor: pointer;
    &:hover {
      color: #0056b3;
    }
  }
  &__showTrigger {
    position: absolute;
    left: -30px;
    top: 50%;
    width: 15px;
    animation: expandTriggerShow .3s;
    @include breakpoint(max-width 900px) {
      left: -20px;
    }
    svg {
      width: 15px;
      height: 40px;
      padding-top: 8px;
    }
    &._hiddenTrigger {
      opacity: 0;
      animation: expandTriggerHide .3s;
    }
  }
  &__hideTrigger {
    display: block;
    width: 100%;
    border-bottom: 1px solid $borderColor1;
    font-size: 11px;
    padding: {
      left: 4px;
      top: 10px;
      bottom: 10px;
    }
    svg {
      vertical-align: -4px;
    }
  }

  @keyframes switcherShow {
    from { margin-right: -50px; }
    to { margin-right: 0; }
  }
  @keyframes switcherHide {
    from { margin-right: 0px; }
    to { margin-right: -50px; }
  }

  &-switcher {
    width: 50px;
    height: 100vh;
    margin-right: -50px;
    border-left: 1px solid $borderColor1;
    animation: switcherHide .3s;
    &__link {
      display: block;
      width: 100%;
      height: 50px;
      text-align: center;
      line-height: 50px;
      border-bottom: 1px solid $borderColor1;
    }
    &._expanded {
      margin-right: 0;
      animation: switcherShow .3s;
    }
  }
  &-content {
    display: none;
    position: relative;
    position: absolute;
    right: 50px;
    top: 0px;
    width: 280px;
    > div {
      position: absolute;
      top: 37px;
      height: calc(100vh - 37px);
      width: 100%;
      overflow: hidden;
      overflow-y: auto;
      padding: {
        top: 50px;
        bottom: 20px;
        left: 10px;
        right: 10px;
      }
      background: white;
      border-left: 1px solid $borderColor1;
    }
    &._active {
      display: block;
    }
    &__collapse {
      position: absolute;
      z-index: 1;
      background: white;
      left: 0;
      top: 0;
      width: 100%;
      padding: 8px 10px;
      padding-top: 7px;
      font-size: 14px;
      border-bottom: 1px solid $borderColor1;
      border-left: 1px solid $borderColor1;
      svg {
        vertical-align: -2px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Toolbar/index.vue